import { Component, Element, Host, Prop, State, h, Event, EventEmitter } from '@stencil/core';
import { Store } from '../../models/stores/Store';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { formSubmissionService, ContactFormData } from '../../utils/services/formSubmissionService';
import { z } from 'zod';
import { ASL_StoreInfoCardProps } from '../aico-store-locator-store-info-card/aico-store-locator-store-info-card';
import { brandService } from '../../utils/services/brandService';
import { translationService, TranslationsProps } from '../../utils/services/translationService';

export const ASL_ContactFormProps = z.object({
  componentColors: ComponentColors.optional(),
  translationsProps: TranslationsProps.optional(),

  ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
});
export type ASL_ContactFormProps = z.infer<typeof ASL_ContactFormProps>;


@Component({
  tag: 'aico-store-locator-contact-form',
  styleUrl: 'aico-store-locator-contact-form.css',
  shadow: true,
})
export class AicoStoreLocatorContactForm {

  @Element() el!: HTMLElement;

  /** Controls whether the contact form popup is open */
  @Prop({ mutable: true }) open: boolean = false;

  /** Store data to display and contact */
  @Prop() store?: Store;

  /** Custom component colors */
  @Prop() componentColors?: ComponentColors;

  @Prop() props?: ASL_ContactFormProps;


  @State() name: string = '';
  @State() email: string = '';
  @State() message: string = '';
  @State() isMobile: boolean = false;
  @State() isSubmitting: boolean = false;
  @State() nameError: string = '';
  @State() emailError: string = '';
  @State() messageError: string = '';
  @State() formSuccess: boolean = false;
  @State() formError: string = '';
  @State() countdown: number = 0;

  @Event() formClosed!: EventEmitter<void>;

  private mediaQuery!: MediaQueryList;
  private mediaQueryListener!: (e: MediaQueryListEvent) => void;
  private unsubscribeController!: AbortController;

  async componentWillLoad() {
    if (this.props) {
      const parsedProps = ASL_ContactFormProps.parse(this.props);
      this.componentColors = parsedProps.componentColors ?? this.componentColors;
    }

    await translationService.initComponentLoad('aico-store-locator-contact-form');

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }

    this.mediaQuery = window.matchMedia('(max-width: 768px)');
    this.isMobile = this.mediaQuery.matches;
    this.mediaQueryListener = (e: MediaQueryListEvent) => {
      this.isMobile = e.matches;
    };
    this.unsubscribeController?.abort();
    this.unsubscribeController = new AbortController();
    this.mediaQuery.addEventListener('change', this.mediaQueryListener, {signal: this.unsubscribeController.signal});
  }

  private t(key: string, locale?: string): string {
    return translationService.getTranslation('aico-store-locator-contact-form', key, locale);
  }

  disconnectedCallback() {
    this.unsubscribeController?.abort();
  }

  private closeForm = () => {
    this.open = false;

    // Reset form
    setTimeout(() => {
      this.name = '';
      this.email = '';
      this.message = '';
      this.nameError = '';
      this.emailError = '';
      this.messageError = '';
      this.formError = '';
      this.formSuccess = false;
      this.countdown = 0;
    }, 300);
    this.formClosed.emit();
  }

  private handleSubmit = async (e: Event) => {
    e.preventDefault();

    this.nameError = '';
    this.emailError = '';
    this.messageError = '';
    this.formError = '';

    let hasErrors = false;

    if (!this.name.trim()) {
      this.nameError = this.t('Please enter your name.');
      hasErrors = true;
    }

    if (!this.email.trim()) {
      this.emailError = this.t('Please enter your email address.');
      hasErrors = true;
    } else {
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email)) {
        this.emailError = this.t('Please enter a valid email address.');
        hasErrors = true;
      }
    }

    if (!this.message.trim()) {
      this.messageError = this.t('Please type your message.');
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    if (!this.store) {
      this.formError = 'Store information is missing. Please try again.';
      return;
    }

    this.isSubmitting = true;

    try {
      const formData: ContactFormData = {
        addressId: this.store.id,
        name: this.name,
        email: this.email,
        message: this.message
      };

      await formSubmissionService.submitContactForm(formData);

      this.formSuccess = true;

      this.countdown = 3;

      const countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(countdownInterval);
          this.closeForm();
        }
      }, 1000);

    } catch (error) {
      if (error instanceof Error) {
        console.error('[aico-store-locator] Form submission error:', error);
      }
      this.formError = this.t('An error occurred. Please try again later.');

    } finally {
      this.isSubmitting = false;
    }
  }

  render() {
    return (
      <Host>
        <div class={`fixed inset-0 z-[200] overflow-hidden transition-opacity duration-300 ${this.open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
          {/* Overlay */}
          <div
            class={`absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ${this.open ? 'opacity-100' : 'opacity-0'}`}
            onClick={this.closeForm}
          ></div>

          {/* Popup container */}
          <div
            class={`
              absolute bg-[--background-color] shadow-xl rounded-lg overflow-hidden
              transition-all duration-300 ease-in-out flex flex-col
              ${this.isMobile ? 'inset-0 w-full h-full' : 'max-w-md w-full max-h-[70vh] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'}
              ${this.open ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
              [&::-webkit-scrollbar]:w-2
              [&::-webkit-scrollbar-track]:rounded-full
              [&::-webkit-scrollbar-track]:bg-[--scrollbar-track-color]
              [&::-webkit-scrollbar-thumb]:rounded-full
              [&::-webkit-scrollbar-thumb]:bg-[--scrollbar-thumb-color]
            `}
          >
            {/* Header - fixed at top */}
            <div class="flex flex-none justify-between items-center p-4 border-gray-200 border-b">
              <h3 class="font-medium text-[--primary-text-color] text-lg">
                {this.t('Write us')}
              </h3>
              <button
                onClick={this.closeForm}
                class="hover:bg-[--background-hover-color] p-2 rounded-full text-[--secondary-text-color] transition-colors"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable content area */}
            <div class="flex-1 p-4 overflow-y-auto">
              {this.store && <aico-store-locator-store-info-card
                refKey={this.store.id}
                organisationName={this.store.organisationName || 'Unknown Store'}
                addressLine={this.store.addressLine1 || ''}
                zipCode={this.store.zipCode || ''}
                city={this.store.city || ''}
                distance={this.store.distance}
                brandsLogos={brandService.getLogosOrderedByPriority(this.store?.crmAddress?.storeLocatorBrandIds)}
                props={this.props?.ASL_StoreInfoCardProps}
              />}

              {this.formSuccess ? (
                <div class="p-4 text-green-600 text-center success-animation">
                  <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto mb-2 w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <p class="font-medium text-lg">{this.t('Thank you for your message!')}</p>
                  <p>{this.t('We will get back to you as soon as possible.')}</p>
                  <p class="mt-2 text-sm">({this.t('Closes in')} {this.countdown} {this.t('seconds')})</p>

                  <button
                    onClick={this.closeForm}
                    class="bg-[--primary-button-background-color] hover:bg-[--primary-button-hover-color] mt-4 px-4 py-2 rounded-full font-medium text-[--primary-button-text-color] transition-colors"
                  >
                    {this.t('Close')}
                  </button>
                </div>
              ) : (
                <form onSubmit={this.handleSubmit}>
                  {this.formError && (
                    <div class="bg-red-100 mb-4 p-3 rounded text-red-700">
                      {this.formError}
                    </div>
                  )}

                  <div class="mb-4">
                    <label htmlFor="name" class="block mb-1 text-[--secondary-text-color]">
                      {this.t('Name')}
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={this.name}
                      onInput={(e) => this.name = (e.target as HTMLInputElement).value}
                      class={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[--primary-button-background-color] focus:border-transparent
                        ${this.nameError ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={this.isSubmitting}
                    />
                    {this.nameError && (
                      <p class="mt-1 text-red-600 text-xs">{this.nameError}</p>
                    )}
                  </div>

                  <div class="mb-4">
                    <label htmlFor="email" class="block mb-1 text-[--secondary-text-color]">
                      {this.t('E-mail')}
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={this.email}
                      onInput={(e) => this.email = (e.target as HTMLInputElement).value}
                      class={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[--primary-button-background-color] focus:border-transparent
                        ${this.emailError ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={this.isSubmitting}
                    />
                    {this.emailError && (
                      <p class="mt-1 text-red-600 text-xs">{this.emailError}</p>
                    )}
                  </div>

                  <div class="mb-4">
                    <label htmlFor="message" class="block mb-1 text-[--secondary-text-color]">
                      {this.t('How can we help you?')}
                    </label>
                    <textarea
                      id="message"
                      value={this.message}
                      onInput={(e) => this.message = (e.target as HTMLTextAreaElement).value}
                      rows={4}
                      class={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[--primary-button-background-color] focus:border-transparent
                        ${this.messageError ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={this.isSubmitting}
                    ></textarea>
                    {this.messageError && (
                      <p class="mt-1 text-red-600 text-xs">{this.messageError}</p>
                    )}
                  </div>

                  <div class="mt-6">
                    <button
                      type="submit"
                      class="bg-[--primary-button-background-color] hover:bg-[--primary-button-hover-color] disabled:opacity-50 px-4 py-3 rounded-full w-full font-medium text-[--primary-button-text-color] transition-colors disabled:cursor-not-allowed"
                      disabled={this.isSubmitting}
                    >
                      {this.isSubmitting ? this.t('Sending...'): this.t('Send')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
